import type { Module } from "components/Admin/Organizations/types/Module.type";
import type { ModuleSelectorConfig } from "./Types";

/**
 * Applies a chart's category scoping to an already-resolved module pool.
 *
 * This is deliberately **terminal**: when the filter empties the pool it returns `[]` and does not
 * widen back to the unfiltered list. The surrounding pool resolvers do fall back to broader
 * catalogs when their own filters come up empty, and inheriting that behavior here would be a bug —
 * a tenant with no module in the requested category would silently resolve to a module outside it,
 * which the backend then rejects. The chart would look correctly empty while the store and the
 * persisted vizOptions held the wrong module.
 */
export const filterModulesByCategory = <
    T extends Pick<Module, "moduleCategoryName">,
>(
    modules: T[],
    moduleSelector: ModuleSelectorConfig | undefined,
): T[] => {
    const categories = moduleSelector?.categories;
    if (!categories?.length) {
        return modules;
    }

    const allowed = new Set(
        categories.map((category) => category.toLowerCase()),
    );

    return modules.filter((module) =>
        allowed.has((module.moduleCategoryName ?? "").toLowerCase()),
    );
};

/**
 * Whether the module selector should render. With `hideWhenSingleOption` there is no choice to make
 * at one option or none, so the control is hidden — the chart still resolves and requests that
 * module, so hiding the dropdown never changes what is queried.
 */
export const shouldShowModuleSelector = (
    moduleSelector: ModuleSelectorConfig | undefined,
    pool: Module[],
): boolean => (moduleSelector?.hideWhenSingleOption ? pool.length > 1 : true);
